import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import type { Zone, Location, SerializedItem } from '../../types';

interface SerializedItemTransferModalProps {
  open: boolean;
  onClose: () => void;
  onTransfer: (locationId: string) => Promise<void>;
  item: SerializedItem | null;
  zones: Zone[];
  locations: Location[];
  isLoading?: boolean;
}

export function SerializedItemTransferModal({
  open,
  onClose,
  onTransfer,
  item,
  zones,
  locations,
  isLoading,
}: SerializedItemTransferModalProps) {
  const [selectedZoneId, setSelectedZoneId] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState('');

  // Reset when modal opens with new item
  useEffect(() => {
    if (open && item) {
      setSelectedZoneId('');
      setSelectedLocationId('');
    }
  }, [open, item]);

  const filteredLocations = selectedZoneId
    ? locations.filter((l) => l.zone_id === selectedZoneId && l.id !== item?.location_id)
    : locations.filter((l) => l.id !== item?.location_id);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLocationId) return;

    try {
      await onTransfer(selectedLocationId);
      toast.success('Item transferred successfully');
      onClose();
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Unknown error occurred';
      toast.error(`Failed to transfer item: ${message}`);
      console.error('Error transferring item:', error);
      // Keep form open for retry
    }
  };

  if (!item) return null;

  const currentZone = zones.find(z =>
    locations.find(l => l.id === item.location_id)?.zone_id === z.id
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Transfer Serialized Item</DialogTitle>
          <DialogDescription>
            Move this device to a new location.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Item Info */}
          <div className="p-3 bg-muted rounded-lg space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Serial Number</span>
              <span className="font-mono font-medium">{item.serial_number}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">SKU</span>
              <span className="font-medium">{item.sku_code}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Current Location</span>
              <Badge variant="outline" className="font-mono">
                {item.full_location_code || 'Unknown'}
              </Badge>
            </div>
          </div>

          {/* New Zone */}
          <div>
            <label className="text-sm font-medium">New Zone</label>
            <Select value={selectedZoneId} onValueChange={(v) => {
              setSelectedZoneId(v);
              setSelectedLocationId('');
            }}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select zone" />
              </SelectTrigger>
              <SelectContent>
                {zones.map((zone) => (
                  <SelectItem key={zone.id} value={zone.id}>
                    <div className="flex items-center gap-2">
                      {zone.color && (
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: zone.color }}
                        />
                      )}
                      <span>
                        {zone.code} - {zone.name}
                        {zone.id === currentZone?.id && ' (current)'}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* New Location */}
          <div>
            <label className="text-sm font-medium">New Location *</label>
            <Select
              value={selectedLocationId}
              onValueChange={setSelectedLocationId}
              disabled={filteredLocations.length === 0}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder={
                  filteredLocations.length === 0
                    ? 'No available locations'
                    : 'Select bin'
                } />
              </SelectTrigger>
              <SelectContent>
                {filteredLocations.map((location) => {
                  const zone = zones.find(z => z.id === location.zone_id);
                  return (
                    <SelectItem key={location.id} value={location.id}>
                      {zone?.code}-{location.bin_code}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || !selectedLocationId}
            >
              {isLoading ? 'Transferring...' : 'Transfer'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
